
import React, { useState, useEffect } from 'react';
import { X, User, Lock, Briefcase, ShieldCheck, UserCheck, Package, Eye, EyeOff } from 'lucide-react';
import { CashierUser, UserRole } from '../../types';
import { useToast } from '../../contexts/ToastContext';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (user: CashierUser) => void;
  user?: CashierUser | null;
}

const UserModal: React.FC<UserModalProps> = ({ isOpen, onClose, onSave, user }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: 'Cashier' as UserRole,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const { addToast } = useToast();

  const isEditMode = !!user;

  useEffect(() => {
    if (isOpen) {
      setFormData({
        username: user?.username ?? '',
        password: '',
        confirmPassword: '',
        role: user?.role ?? 'Cashier',
      });
      setShowPassword(false);
      setShowConfirmPassword(false);
      setPasswordError('');
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    console.log(formData);
    e.preventDefault();

    if (formData.password && formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setPasswordError('');

    const URL = isEditMode
      ? `${process.env.USER_BASE_URL}/${formData.username}`
      : process.env.USER_BASE_URL!;
    const METHOD = isEditMode ? 'PUT' : 'POST';

    // POST body uses the exact field names the backend expects
    const CREATE_BODY = {
      username: formData.username,
      password: formData.password,
      role: formData.role,
    };
    // PUT body — username is immutable (used as URL param), only role can change
    const UPDATE_BODY = { role: formData.role };

    try {
      // If editing and a new password was provided, change it first
      if (isEditMode && formData.password) {
        const pwRes = await fetch(
          `${process.env.USER_BASE_URL}/change-password/${formData.username}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newPassword: formData.password }),
          }
        );
        const pwData = await pwRes.json();
        if (!pwData.OK) {
          addToast('error', 'Password Error', pwData.MSG ?? 'Failed to update password.');
          return;
        }
      }
      console.log(CREATE_BODY);
      console.log(METHOD),
      console.log(URL);
      const res = await fetch(URL, {
        method: METHOD,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEditMode ? UPDATE_BODY : CREATE_BODY),
      });
      const data = await res.json();

      if (data.OK && isEditMode) {
        // Prefer fresh data from the response; fall back to local state
        const raw = data.DATA;
        const updated: CashierUser = raw?.userId
          ? {
              _id: raw.userId,
              id: raw.userId,
              username: raw.userName,
              name: raw.userName,
              email: raw.email || '',
              role: raw.role as UserRole,
              phoneNumber: raw.phoneNumber,
            }
          : {
              ...(user as CashierUser),
              role: formData.role,
            };
        addToast('success', 'Staff Updated', `${formData.username} saved.`);
        onSave(updated);
        onClose();
      } else if (!isEditMode) {
        if (data.OK) {
          // Build a local CashierUser from what we sent
          const created: CashierUser = {
            _id: data.DATA?.userId ?? Math.random().toString(36).substr(2, 9).toUpperCase(),
            id: data.DATA?.userId,
            username: formData.username,
            name: formData.username,
            email: '',
            role: formData.role,
          };
          addToast('success', 'Staff Registered', `${formData.username} added.`);
          onSave(created);
          onClose();
        } else {
          addToast('error', 'Registration Failed', data.MSG ?? 'Could not register staff member.');
        }
      } else {
        addToast('error', 'Save Failed', data.MSG ?? 'Could not save changes.');
      }
    } catch {
      addToast('error', 'Network Error', 'Could not reach the server. Try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col transition-colors duration-200">

        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? 'Edit Staff Member' : 'Register New Staff Member'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Username
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
              <input
                required
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase() })}
                className="w-full pl-10 pr-4 py-2.5 bg-white disabled:bg-gray-100 dark:bg-gray-700 dark:disabled:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                placeholder="e.g. sarah_jenkins"
                disabled={isEditMode}
              />
            </div>
            {isEditMode && (
              <p className="text-[9px] text-gray-400 font-bold mt-1 ml-1 uppercase tracking-wide">
                Username cannot be changed
              </p>
            )}
          </div>

          {/* Password row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {isEditMode ? 'New Password' : 'Password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required={!isEditMode}
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  placeholder={isEditMode ? 'Leave blank to keep' : '••••••'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {isEditMode ? 'Confirm New Password' : 'Confirm Password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={18} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  required={!isEditMode}
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, confirmPassword: e.target.value });
                    setPasswordError('');
                  }}
                  className={`w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-700 border rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all ${
                    passwordError
                      ? 'border-red-400 focus:ring-red-500'
                      : 'border-gray-200 dark:border-gray-600 focus:ring-indigo-500'
                  }`}
                  placeholder="••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {passwordError && (
                <p className="text-xs text-red-500 font-bold mt-1">{passwordError}</p>
              )}
            </div>
          </div>

          {/* Access Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Access Level
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: 'Cashier' })}
                className={`flex items-center gap-3 p-2.5 rounded-xl border-2 transition-all ${
                  formData.role === 'Cashier'
                    ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-600 text-indigo-700 dark:text-indigo-400 shadow-sm'
                    : 'bg-white dark:bg-gray-700 border-gray-100 dark:border-gray-600 text-gray-500'
                }`}
              >
                <UserCheck size={18} />
                <span className="font-bold text-[10px] uppercase">Cashier</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: 'Warehouse Manager' })}
                className={`flex items-center gap-3 p-2.5 rounded-xl border-2 transition-all ${
                  formData.role === 'Warehouse Manager'
                    ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-600 text-purple-700 dark:text-purple-400 shadow-sm'
                    : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-600 text-gray-500'
                }`}
              >
                <Package size={18} />
                <span className="font-bold text-[10px] uppercase">Warehouse Manager</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: 'Admin' })}
                className={`flex items-center gap-3 p-2.5 rounded-xl border-2 transition-all ${
                  formData.role === 'Admin'
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-600 text-amber-700 dark:text-amber-400 shadow-sm'
                    : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-600 text-gray-500'
                }`}
              >
                <ShieldCheck size={18} />
                <span className="font-bold text-[10px] uppercase">Admin</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, role: 'Viewer' })}
                className={`flex items-center gap-3 p-2.5 rounded-xl border-2 transition-all ${
                  formData.role === 'Viewer'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-600 text-emerald-700 dark:text-emerald-400 shadow-sm'
                    : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-600 text-gray-500'
                }`}
              >
                <Eye size={18} />
                <span className="font-bold text-[10px] uppercase">Viewer</span>
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200 dark:shadow-none"
            >
              {isEditMode ? 'Update Staff' : 'Register Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
